import React from 'react';
import styled from '@emotion/styled';
import { EmotionBreakpoint } from 'dw-us-ui';

//
// 1) STYLED COMPONENTS
//
// ${EmotionBreakpoint('tablet')} {
// ${EmotionBreakpoint('mobile')} {

const BenefitsContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 40px 6%;
	max-width: 100%;
	margin: 0 auto;
	${EmotionBreakpoint('mobile')} {
		padding: 32px 16px;
		display: flex;
		flex-direction: column;
		align-items: center;
	}
	${EmotionBreakpoint('tablet')} {
		padding: 32px 24px;
		display: flex;
		flex-direction: column;
		align-items: center;
	}
`;

// Headline for the benefits section
const BenefitsHeading = styled.h2`
	color: #2b2a2f;
	margin-bottom: 40px;
	margin-top: 20px;
	text-align: center;
	font-family: 'Escrow Banner Compressed', sans-serif;
	font-size: 40px;
	font-style: normal;
	font-weight: 500;
	line-height: 112%;
	.fox & {
		color: var(--Base-Colors-Black, #222);
		text-align: center;
		font-family: Roboto;
		font-size: 30px;
		font-style: normal;
		font-weight: 900;
		line-height: 105%;
		letter-spacing: -1.2px;
	}
	.bon & {
		color: var(--Base-Colors-Black, #222);
		text-align: center;
		font-family: 'FuturaPT';
		font-size: 32px;
		font-style: normal;
		font-weight: 600;
		line-height: 40px;
		letter-spacing: 1.28px;
		text-transform: uppercase;
	}

	.law & {
		font-family: 'Oswald';
	}
	.osw & {
		font-family: 'Neue Aachen Pro';
	}
	.npr & {
		font-family: 'Gotham';
		font-size: 30px;
	}

	${EmotionBreakpoint('mobile')} {
		margin-top: 0px;
		margin-bottom: 32px;
		text-align: center;
		font-size: 32px;
		.osw & {
			font-size: 30px;
		}
	}
	${EmotionBreakpoint('tablet')} {
		margin-top: 0px;
		margin-bottom: 32px;
		text-align: center;
		font-size: 32px;
	}
`;

// The grid/list of benefit items
const BenefitsList = styled.div`
	display: flex;
	flex-direction: row;
	gap: 20px;
	justify-content: space-between;

	${EmotionBreakpoint('mobile')} {
		flex-direction: column;
		gap: 24px;
	}
	${EmotionBreakpoint('tablet')} {
		flex-direction: column;
		gap: 24px;
	}
`;

const BenefitItem = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;
	border-radius: 8px;
	gap: 12px;
	// Default: 4 pods
	flex: 1 1 calc(25% - 20px);
	${({ podCount }) =>
		podCount === 3 &&
		`
        flex: 1 1 calc(33.33% - 20px);
    `}
	${({ podCount }) =>
		podCount === 2 &&
		`
        flex: 1 1 calc(50% - 20px);
    `}
    ${EmotionBreakpoint('mobile')}, ${EmotionBreakpoint('tablet')} {
		flex-direction: row;
		flex: 1 1 100%;
		margin: 0 auto;
		gap: 16px;
	}

	.icon-title {
		display: none;
		${EmotionBreakpoint('mobile')} {
			display: flex;
			flex-direction: column;
			text-align: left;
			.fox & {
				width: 84%;
			}
		}
		${EmotionBreakpoint('tablet')} {
			display: flex;
			flex-direction: column;
			text-align: left;
		}
	}

	h3 {
		color: #2b2a2f;
		font-family: Escrow;
		font-size: 24px;
		font-style: normal;
		font-weight: 400;
		line-height: 28px;
		width: 100%;
		max-width: 100%;

		.bon & {
			color: #222;
			font-family: 'FuturaPT';
			font-size: 22px;
			font-style: normal;
			font-weight: 450;
			line-height: 28px;
		}
		.fox & {
			color: var(--Base-Colors-Black, #222);
			font-family: Roboto;
			font-size: 16px;
			font-style: normal;
			font-weight: 500;
			line-height: 140%;
			letter-spacing: 0.16px;
		}
		.law & {
			font-family: 'Noto Serif' !important;
		}
		.osw & {
			font-family: 'Neue Haas Grotesk Text Pro' !important;
			font-size: 22px !important;
		}
		.npr & {
			font-family: 'Gotham' !important;
			font-size: 22px !important;
			font-weight: 325 !important;
		}
		${EmotionBreakpoint('mobile')} {
			font-size: 20px;
		}
		${EmotionBreakpoint('tablet')} {
			font-size: 20px;
		}
	}

	p {
		display: none;
		.law & {
			font-family: 'Roboto' !important;
		}
		.osw & {
			font-family: 'Roboto' !important;
		}
		.npr & {
			font-family: 'Roboto' !important;
		}

		${EmotionBreakpoint('mobile')} {
			display: flex;
			color: #2b2a2f;
			font-family: Arial;
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			line-height: 20px;
			width: 100%;
			max-width: 100%;
			.fox & {
				color: var(--Base-Colors-Black, #222);
				font-family: Arial;
				font-size: 14px;
				font-style: normal;
				font-weight: 400;
				line-height: 20px; /* 142.857% */
			}
		}
		${EmotionBreakpoint('tablet')} {
			display: flex;
			color: #2b2a2f;
			font-family: Arial;
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			line-height: 20px;
			width: 100%;
			max-width: 100%;
		}
	}
	.desktop-item {
		display: flex;
		gap: 12px;
		.image-wrap {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 60px;
			height: 60px;
			border-radius: 30px;
			.fox & {
				padding: 15px;
			}
			.bon & {
				background: #827aba;
			}
			.wsj & {
				background: rgb(230, 241, 246);
				height: 56px;
				width: 56px;
				padding: 20px;
			}
			.osw & {
				padding: 20px;
			}
			.LAW & {
				padding: 20px;
			}
			.npr & {
				background: rgb(230, 241, 246);
				padding: 20px;
			}
		}
		.img {
			.fox & {
				max-width: none !important;
			}
		}
		${EmotionBreakpoint('mobile')} {
			.fox & {
				width: 16%;
			}
		}
	}
	.desktop-title {
		display: flex;
		align-items: center;
		${EmotionBreakpoint('mobile')} {
			display: none;
		}
		${EmotionBreakpoint('tablet')} {
			display: none;
		}
		h3 {
			color: #2b2a2f;
			font-family: Escrow;
			font-size: 24px;
			font-style: normal;
			font-weight: 400;
			line-height: 28px;
			width: 100%;
			max-width: 100%;
			text-align: left;

			.bon & {
				color: var(--Base-Colors-Black, #222);
				font-family: 'FuturaPT';
				font-size: 22px;
				font-style: normal;
				font-weight: 450;
				line-height: 28px;
			}
			.fox & {
				color: var(--Base-Colors-Black, #222);
				font-family: Roboto;
				font-size: 18px;
				font-style: normal;
				font-weight: 600;
				line-height: 140%;
			}
		}
	}
	.desktop-body {
		display: flex;
		p {
			display: flex;
			color: #2b2a2f;
			font-family: Arial;
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			line-height: 20px;
			width: 100%;
			max-width: 100%;
			text-align: left;
			.fox & {
				color: var(--Base-Colors-Black, #222);
				font-family: Roboto;
				font-size: 14px;
				font-style: normal;
				font-weight: 400;
				line-height: 130%; /* 18.2px */
			}
			.bon & {
				color: var(--Base-Colors-Black, #222);
				/* BA/Body Small Regular */
				font-family: 'FuturaPT';
				font-size: 16px;
				font-style: normal;
				font-weight: 400;
				line-height: 20px;
			}
		}
		${EmotionBreakpoint('mobile')} {
			display: none;
		}
		${EmotionBreakpoint('tablet')} {
			display: none;
		}
	}
`;

// Container for the highlight section
const HighlightContainer = styled.div`
	margin-top: 40px;

	/* Optional: If you want to style differently from .lp-highlight */
	&.lp-highlight {
		padding: 20px 0;
	}

	.lp-highlight-items {
		display: flex;
		flex-wrap: wrap;
		gap: 20px;
		justify-content: center;
	}

	.lp-highlight-item {
		display: flex;
		width: 375px;
		height: 128px;
		padding: 26px 16px;
		flex-direction: column;
		align-items: flex-start;
		gap: 32px;
		text-align: center;
		/* Example styling—adjust to your liking */
		background-color: #203c5d;
		color: #ffffff;
		border-radius: 4px;
		padding: 20px;
		max-width: 300px;

		.image-wrap {
			margin-bottom: 10px;
		}

		h3 {
			margin: 0 0 10px;
			font-size: 18px;
			width: 60%;
		}

		p {
			font-size: 14px;
			line-height: 1.4;
		}
	}
`;

//
// 2) MERGED COMPONENT
//
const BenefitsSection = ({ contentfulData }) => {
	// Data destructuring from props
	const {
		benefitsColumnsHeadline,
		benefitsItemTitles = [],
		benefitsItemBody = [],
		benefitsItemIcons = [],
	} = contentfulData;

	return (
		<BenefitsContainer>
			{/* === BENEFITS HEADLINE === */}
			{benefitsColumnsHeadline && (
				<BenefitsHeading>{benefitsColumnsHeadline}</BenefitsHeading>
			)}

			{/* === BENEFITS LIST === */}
			<BenefitsList>
				{benefitsItemTitles?.map((title, index) => (
					<BenefitItem
						key={index}
						podCount={benefitsItemTitles?.length} // dynamically pass count
					>
						{benefitsItemIcons[index]?.fields?.file?.url && (
							<div className='desktop-item'>
								<div className='image-wrap'>
									<img
										src={`https:${benefitsItemIcons[index].fields.file.url}`}
										alt={title || 'Benefit Icon'}
										className='img'
									/>
								</div>
								<div className='desktop-title'>
									<h3>{title}</h3>
								</div>
							</div>
						)}
						<div className='icon-title'>
							<h3>{title}</h3>
							{benefitsItemBody[index] && <p>{benefitsItemBody[index]}</p>}
						</div>
						<div className='desktop-body'>
							{benefitsItemBody[index] && <p>{benefitsItemBody[index]}</p>}
						</div>
					</BenefitItem>
				))}
			</BenefitsList>
		</BenefitsContainer>
	);
};

export default BenefitsSection;
